import type { LobbyServerEvent, WireConditionEntry } from '@danda/shared';
import { ActiveNPCEnum } from '@danda/shared';
import { cleanNpcResponse } from '@/lib/filters/cleanNpcResponse';
import { getSpecialNpcEmoji } from '@danda/shared/utils/avatar-emoji';
import type { ChatMessageType } from '@/lib/markdown';
import { getStaffAvatarUrl } from '@/lib/media';
import { resolveCharacterAvatarUrl, resolveCharacterPortraitUrl } from './resolve-avatar';
import type { EventHandlerContext } from './lobby-event-types';
import { addCompletedStreamId, addOrEnqueueMessage, msgId } from './stream-utils';
import { getPersistedActiveChatRoom, isSafeToRestoreRoom } from '../lobbyStore.chat';

/** Tavian's pre-made avatar URL */
const TAVIAN_AVATAR_URL = getStaffAvatarUrl('tavian-avatar.webp');

/**
 * Drop a party's post-session typing flag. Called on party teardown
 * (left / deleted / dissolved) so a `lobby:party-typing: true` that never got
 * its matching `false` (backend task panic, or the party torn down mid-recovery)
 * can't strand the dots — or leak them onto a different party that happens to
 * reuse the room slot. (`lobby:session-state` resets the whole map on reconnect.)
 */
function clearPartyTyping(set: EventHandlerContext['set'], partyId: string): void {
  set((s) => {
    if (!(partyId in s.partyTyping)) return {};
    const { [partyId]: _removed, ...rest } = s.partyTyping;
    return { partyTyping: rest };
  });
}

/**
 * Map a staff NPC id to its dedicated `ChatMessageType` so the chat
 * renderer can pick the right voice styling. Returns `null` for any id
 * that isn't a known staff character — callers fall back to `'npc'` or
 * `'user'` based on the wire `isNpc` flag.
 *
 * Centralised here because both `lobby:party-chat` and
 * `lobby:party-chat-npc` handlers need the same mapping after the
 * 2026-05-28 typewriter fix (the WS variants aren't NPC-pure).
 */
function staffMessageType(id: string): ChatMessageType | null {
  switch (id) {
    case ActiveNPCEnum.TAVIAN:
      return 'tavian';
    case ActiveNPCEnum.ALDRIC:
      return 'aldric';
    case ActiveNPCEnum.KIRA:
      return 'kira';
    case ActiveNPCEnum.GROLT:
      return 'grolt';
    default:
      return null;
  }
}

export function handlePartyEvents(event: LobbyServerEvent, ctx: EventHandlerContext): boolean {
  const { get, set } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:party-created':
      state.addParty(event.party);
      return true;

    case 'lobby:party-updated': {
      console.log('[party-events] party-updated received:', event.party.id, event.party.name);
      const prevParty = state.parties.find((p) => p.id === event.party.id);
      const prevMembers = prevParty?.memberCharacterIds || [];
      const newMembers = event.party.memberCharacterIds;
      const partyRoomId = `party-${event.party.id}`;
      const memberCount = event.party.members?.length ?? event.party.memberCharacterIds.length;
      const renamed = !!prevParty && prevParty.name !== event.party.name;
      if (renamed) {
        console.log('[party-events] Party renamed from', prevParty.name, 'to', event.party.name);
      }

      // Coalesce parties + chatRooms updates into one zustand commit.
      // Previously this fired 3 separate setState calls (replaceParty/addParty,
      // updateChatRoomParticipants, updateChatRoom) — each one re-rendered every
      // store subscriber. Combining them cuts the cascade to a single commit per
      // party-updated event.
      set((s) => {
        let parties = s.parties;
        if (prevParty) {
          // Inline replaceParty merge logic — preserves optimistically-set
          // partyImageUrl + members' portraitUrl/avatarUrl when the incoming
          // snapshot omits them (matches lobbyStore.parties.ts replaceParty).
          parties = parties.map((p) => {
            if (p.id !== event.party.id) return p;
            const mergedMembers = event.party.members?.map((m) => {
              const prev = p.members?.find((e) => e.id === m.id);
              if (!prev) return m;
              return {
                portraitUrl: prev.portraitUrl,
                avatarUrl: prev.avatarUrl,
                ...m,
              };
            });
            return {
              ...event.party,
              partyImageUrl: event.party.partyImageUrl ?? p.partyImageUrl,
              members: mergedMembers ?? event.party.members,
            };
          });
        } else if (!s.parties.some((p) => p.id === event.party.id)) {
          parties = [...s.parties, event.party];
        }

        const chatRooms = s.chatRooms.map((r) =>
          r.id === partyRoomId
            ? {
                ...r,
                participants: event.party.memberCharacterIds,
                memberCount,
                ...(renamed ? { name: event.party.name } : {}),
              }
            : r
        );

        // SOT mirror (party-updated → characters):
        // The canonical HP/maxHP/conditions/exhaustion/resurrection-penalty
        // for a character is `state.characters[i].session.*` /
        // `permanent.max_hp`. PartyUpdated carries a denormalized cache
        // (`members[].currentHp/maxHp/conditions/exhaustionLevel/
        // resurrectionPenalty`); when the backend refreshes that cache
        // (e.g. post-recovery, post-long-rest), we mirror back into the
        // Character store so every panel reflects the new values, not just
        // the acting player's own screen.
        //
        // **Only mirror members whose value ACTUALLY CHANGED** in this
        // event vs the prior party snapshot. PartyUpdated also fires for
        // unrelated events (e.g. another member joining) where the
        // denormalized cache for an existing member may carry a stale value
        // the backend never refreshed. Without the change-check, that stale
        // value would clobber a session field set moments earlier by a
        // CharacterUpdated event (Codex review #177 follow-up — the same
        // clobber risk now applies to conditions/exhaustion/resurrection
        // penalty since they ride the same cache).
        //
        // The matching server-side invariant — that every PartyUpdated
        // broadcast carries fresh member HP — is enforced by calling
        // `refresh_party_member_states` (or `enrich_party_member_states`)
        // before broadcast. The 2026-05-22 review found 5 sites missing
        // this call (join/leave/vote/kick/invite); see
        // `backend/lobby-server/src/lobby/handlers/party/...`.
        interface MemberSync {
          hp?: number;
          maxHp?: number;
          conditions?: WireConditionEntry[];
          exhaustionLevel?: number;
          resurrectionPenalty?: number;
        }
        const memberHpById = new Map<string, MemberSync>();
        const prevMembersById = new Map(
          (prevParty?.members ?? []).map((pm) => [pm.id, pm] as const)
        );
        for (const m of event.party.members ?? []) {
          const prev = prevMembersById.get(m.id);
          const isNew = prev == null;
          // For existing members, only mirror when the value ACTUALLY
          // changed vs the prior snapshot — that's how we avoid a stale
          // member.currentHp from an unrelated PartyUpdated (e.g. another
          // member joining) clobbering a fresh session.hp set moments
          // earlier by CharacterUpdated.
          const hpChanged = !isNew && prev.currentHp !== m.currentHp;
          const maxHpChanged = !isNew && prev.maxHp !== m.maxHp;
          // Conditions are an array — compare normalized (missing == empty,
          // since the wire omits the field entirely when the array is
          // empty). Exhaustion/resurrection-penalty are scalars where
          // missing == 0/cleared, same normalization.
          const conditionsChanged =
            !isNew && JSON.stringify(prev.conditions ?? []) !== JSON.stringify(m.conditions ?? []);
          const exhaustionChanged =
            !isNew && (prev.exhaustionLevel ?? 0) !== (m.exhaustionLevel ?? 0);
          const resurrectionPenaltyChanged =
            !isNew && (prev.resurrectionPenalty ?? 0) !== (m.resurrectionPenalty ?? 0);
          // For brand-new members (no prev to diff), seed the mirror so
          // the canonical Character store picks up the latest backend
          // snapshot. If state.characters[m.id] doesn't exist, the
          // s.characters.map below is a no-op for that id.
          const seedNew =
            isNew &&
            (m.currentHp != null ||
              m.maxHp != null ||
              (m.conditions?.length ?? 0) > 0 ||
              m.exhaustionLevel != null ||
              m.resurrectionPenalty != null);
          if (
            hpChanged ||
            maxHpChanged ||
            conditionsChanged ||
            exhaustionChanged ||
            resurrectionPenaltyChanged ||
            seedNew
          ) {
            memberHpById.set(m.id, {
              hp: (hpChanged || seedNew) && m.currentHp != null ? m.currentHp : undefined,
              maxHp: (maxHpChanged || seedNew) && m.maxHp != null ? m.maxHp : undefined,
              conditions: conditionsChanged || seedNew ? (m.conditions ?? []) : undefined,
              exhaustionLevel: exhaustionChanged || seedNew ? (m.exhaustionLevel ?? 0) : undefined,
              resurrectionPenalty:
                resurrectionPenaltyChanged || seedNew ? (m.resurrectionPenalty ?? 0) : undefined,
            });
          }
        }
        // Workaround for the wire-type gap (#193): on a 0 → positive
        // recovery transition, session.conditions may still hold a stale
        // 'unconscious' / 'dying' entry that getCharacterStatus() would
        // surface as the badge — because PartyMemberInfo can't carry
        // condition changes, only HP. Strip on that exact transition only.
        // Narrowing the trigger to **0 → positive** keeps a legitimate
        // spell-induced unconscious (e.g. Sleep at HP=5 taking damage to
        // HP=1) intact, since the prev HP was positive — only true
        // recovery from 0 strips. Until #193 lands (recovery paths emit
        // CharacterUpdated for status/condition changes), this keeps the
        // badge consistent with the new HP.
        const stripUnconsciousOnRecovery = (
          conditions: unknown,
          prevHp: number | undefined,
          newHp: number | undefined
        ): unknown => {
          if (prevHp == null || prevHp > 0) return conditions;
          if (newHp == null || newHp <= 0) return conditions;
          if (!Array.isArray(conditions) || conditions.length === 0) return conditions;
          const name = (c: unknown): string => {
            if (typeof c === 'string') return c.toLowerCase();
            if (c && typeof c === 'object' && 'name' in c) {
              return String((c as { name: unknown }).name).toLowerCase();
            }
            return '';
          };
          const filtered = conditions.filter((c) => {
            const n = name(c);
            return n !== 'unconscious' && n !== 'dying';
          });
          return filtered.length === conditions.length ? conditions : filtered;
        };
        const characters =
          memberHpById.size === 0
            ? s.characters
            : s.characters.map((c) => {
                const update = memberHpById.get(c.id);
                if (!update) return c;
                // Only mirror hp into session when the Character already has
                // a session object — synthesizing one would force-fill
                // required fields (id, etc.) we don't have here.
                let nextSession = c.session;
                if (update.hp != null && c.session) {
                  const sanitized = stripUnconsciousOnRecovery(
                    c.session.conditions,
                    c.session.hp,
                    update.hp
                  );
                  nextSession = {
                    ...c.session,
                    hp: update.hp,
                    ...(sanitized !== c.session.conditions
                      ? { conditions: sanitized as typeof c.session.conditions }
                      : {}),
                  };
                }
                // Authoritative fresh values from this PartyUpdated win over
                // the HP-transition heuristic above (e.g. a long rest clears
                // conditions/exhaustion/resurrection-penalty directly).
                if (
                  c.session &&
                  (update.conditions !== undefined ||
                    update.exhaustionLevel !== undefined ||
                    update.resurrectionPenalty !== undefined)
                ) {
                  nextSession = {
                    ...(nextSession ?? c.session),
                    ...(update.conditions !== undefined
                      ? { conditions: update.conditions as typeof c.session.conditions }
                      : {}),
                    ...(update.exhaustionLevel !== undefined
                      ? { exhaustion_level: update.exhaustionLevel || undefined }
                      : {}),
                    ...(update.resurrectionPenalty !== undefined
                      ? { resurrection_penalty: update.resurrectionPenalty || undefined }
                      : {}),
                  };
                }
                const nextPermanent =
                  update.maxHp != null && c.permanent
                    ? { ...c.permanent, max_hp: update.maxHp }
                    : c.permanent;
                if (nextSession === c.session && nextPermanent === c.permanent) {
                  return c;
                }
                return {
                  ...c,
                  session: nextSession,
                  permanent: nextPermanent,
                };
              });

        return { parties, chatRooms, characters };
      });

      const getChar = (charId: string) => state.lobbyCharacters.find((c) => c.id === charId);
      const getCharName = (charId: string): string => {
        // Check lobbyCharacters first
        const char = getChar(charId);
        if (char?.name) return char.name;
        // Check party members (event.party.members contains the joining member's data)
        const partyMember = event.party.members?.find((m) => m.id === charId);
        if (partyMember?.name) return partyMember.name;
        // Fallback to ID
        return charId;
      };

      // Only show leave messages if we already knew about this party.
      // Joins are intentionally NOT announced here — the NPC's own greeting
      // (and Tavian's invitation flow) already confirm the join; a separate
      // system "X has joined the party!" is redundant.
      if (prevParty) {
        const left = prevMembers.filter((id) => !newMembers.includes(id));

        for (const memberId of left) {
          state.addMessage(partyRoomId, {
            id: msgId('msg'),
            senderId: 'system',
            senderName: 'System',
            content: `👋 ${getCharName(memberId)} has left the party.`,
            timestamp: new Date(),
            type: 'party-leave',
          });
        }
      }
      return true;
    }

    case 'lobby:party-deleted':
      state.removeParty(event.partyId);
      clearPartyTyping(set, event.partyId);
      return true;

    case 'lobby:party-joined': {
      const partyRoomId = `party-${event.party.id}`;
      const existingParty = state.parties.find((p) => p.id === event.party.id);
      if (existingParty) {
        state.replaceParty(event.party.id, event.party);
      } else {
        state.addParty(event.party);
      }
      state.setCurrentParty(event.party.id);
      const existingRoom = state.chatRooms.find((r) => r.id === partyRoomId);
      const partyMemberCount = event.party.members?.length ?? event.party.memberCharacterIds.length;
      if (!existingRoom) {
        state.addChatRoom({
          id: partyRoomId,
          name: event.party.name,
          participants: event.party.memberCharacterIds,
          messages: [],
          unreadCount: 0,
          type: 'party',
          isTavianPresent: true,
          memberCount: partyMemberCount,
        });
      } else {
        state.updateChatRoomParticipants(partyRoomId, event.party.memberCharacterIds);
        state.updateChatRoom(partyRoomId, { memberCount: partyMemberCount });
      }
      // Only switch to party chat if user doesn't have a saved preference for a safe room
      const savedJoinRoom = getPersistedActiveChatRoom();
      if (
        !savedJoinRoom ||
        !isSafeToRestoreRoom(savedJoinRoom) ||
        !state.chatRooms.some((r) => r.id === savedJoinRoom)
      ) {
        state.switchChatRoom(partyRoomId);
      }
      return true;
    }

    case 'lobby:party-left':
      state.setCurrentParty(null);
      // Without this, the left party stayed in `parties[]` forever (still
      // listing this character in memberCharacterIds), so any panel that
      // derives its party via `parties.find(p =>
      // p.memberCharacterIds.includes(selectedCharacter.id))` (PartyPanel,
      // ModulePanel) kept matching the OLD party after creating a new one.
      // Fixed here to mirror the party-dissolved handler below.
      // bug_lobby-party-panel-stale-after-leave-create.md
      state.removeParty(event.partyId);
      state.removeChatRoom(`party-${event.partyId}`);
      state.clearPartyDecision(event.partyId);
      clearPartyTyping(set, event.partyId);
      // Journal is party-scoped; drop the cache so the panel hides until a new
      // party with session history fetches its own journal.
      state.clearLobbyJournal();
      return true;

    case 'lobby:party-dissolved': {
      // Capture ownership BEFORE removeParty — the action nulls currentPartyId
      // when it matches the removed id, so a later check would always be false.
      const wasOwnParty = state.currentPartyId === event.partyId;
      state.removeParty(event.partyId);
      state.removeChatRoom(`party-${event.partyId}`);
      state.clearPartyDecision(event.partyId);
      clearPartyTyping(set, event.partyId);
      // PartyDissolved is broadcast to the whole tavern lobby (see
      // character.rs:853), so this event fires for unrelated parties too.
      // Only clear the journal when it's the user's OWN party.
      if (wasOwnParty) {
        state.clearLobbyJournal();
      }
      return true;
    }

    case 'lobby:party-chat': {
      // Look up character info from party members, lobbyCharacters, or store
      const chatChar = state.lobbyCharacters.find((c) => c.id === event.characterId);
      const party = state.parties.find((p) => p.id === event.partyId);
      const partyMember = party?.members?.find((m) => m.id === event.characterId);
      const storeChar = state.characters.find((c) => c.id === event.characterId);
      // Avatar priority: event's `avatarUrl` (PC's `session.avatar_url`)
      // → event's `portraitUrl` (fallback for PCs whose `avatar_url`
      // isn't generated but who have a portrait) → party-member roster
      // avatar → roster portrait → lobbyCharacters/character store
      // avatar → store portrait. The portrait fallbacks let portrait-
      // only NPCs (e.g. roster speakers in `*votes aye*` auto-vote
      // bubbles) surface their art instead of the generic letter
      // fallback.
      const avatarUrl =
        event.avatarUrl ||
        event.portraitUrl ||
        partyMember?.avatarUrl ||
        partyMember?.portraitUrl ||
        resolveCharacterAvatarUrl(chatChar, storeChar) ||
        resolveCharacterPortraitUrl(chatChar, storeChar);
      // `lobby:party-chat` is NOT NPC-pure on the wire — both PC speech
      // AND NPC narration (NPC `*votes aye*` auto-votes from
      // `trigger_npc_voting_for_decision`, Tavian portal announcements
      // from `stream_tavian_to_party`) ride this variant. Route the
      // message type from `event.isNpc` + the staff-id check so the
      // typewriter gate (`shouldTypewriter`) fires correctly:
      //   - PC speech (`isNpc:false`)        → `'user'`
      //   - Staff (Tavian/Aldric/Kira/Grolt) → matching staff type
      //   - Non-staff NPC                    → `'npc'` (typewriters)
      const msgType: ChatMessageType = event.isNpc
        ? (staffMessageType(event.characterId) ?? 'npc')
        : 'user';
      // Route through the reveal queue so typewriter-eligible NPC
      // bubbles (`*votes aye*` auto-votes, non-staff NPCs) mount via
      // `ProgressiveReveal`. PC speech and Tavian bubbles ALSO go
      // through the queue but render instantly — `ProgressiveReveal`'s
      // `shouldTypewriter` gate short-circuits them, so the only
      // visible cost is one queue-advance tick when the room is
      // active. `addOrEnqueueMessage` falls back to `addMessage` for
      // background rooms, preserving the previous append-directly
      // behavior for non-active party tabs.
      addOrEnqueueMessage(state, `party-${event.partyId}`, {
        id: msgId('msg'),
        senderId: event.characterId,
        senderName: event.characterName,
        content: event.message,
        timestamp: new Date(event.sent_at),
        type: msgType,
        avatar: avatarUrl,
        avatarUrl,
        avatarEmoji: event.avatarEmoji || partyMember?.avatarEmoji || chatChar?.avatarEmoji,
      });
      return true;
    }

    case 'lobby:party-typing': {
      // Tavian's post-session narration sequence (recap, long rest, cooldown)
      // started/ended. The party chat shows the typing dots while this is true
      // and nothing is streaming — filling each multi-second LLM gap. We mirror
      // the backend's true/false bracket verbatim; per-stream hiding is handled
      // at render time by the "not currently streaming" guard, not here.
      const typingPartyId = event.partyId;
      set((s) => ({
        partyTyping: { ...s.partyTyping, [typingPartyId]: event.typing },
      }));
      return true;
    }

    case 'lobby:party-stream-start': {
      const { activeStreams, completedStreamIds } = get();

      // Skip if stream already completed (dedup for out-of-order events on reconnect)
      if (completedStreamIds[event.streamId]) {
        return true;
      }

      // Skip duplicate stream-start events
      if (activeStreams[event.streamId]) {
        console.warn('[party-stream-start] Duplicate stream ignored:', event.streamId);
        return true;
      }

      const roomId = `party-${event.partyId}`;
      const messageId = `party-stream-${event.streamId}`;
      // Look up character emoji (special handling for Tavian)
      const isTavian = event.characterId === ActiveNPCEnum.TAVIAN;
      const streamParty = state.parties.find((p) => p.id === event.partyId);
      const streamMember = streamParty?.members?.find((m) => m.id === event.characterId);
      const streamChar = !isTavian
        ? state.lobbyCharacters.find((c) => c.id === event.characterId)
        : null;
      const tavianEmoji = getSpecialNpcEmoji('tavian') ?? '🧙‍♂️';
      // Prefer partyMember's avatarUrl (from party data), then fall back to lobbyCharacters
      const avatarUrl = isTavian
        ? TAVIAN_AVATAR_URL
        : streamMember?.avatarUrl || streamChar?.avatarUrl || streamChar?.avatar;
      const emoji = isTavian ? tavianEmoji : streamMember?.avatarEmoji || streamChar?.avatarEmoji;
      // `lobby:party-stream-start` carries both Tavian streams (from
      // `stream_tavian_to_party`) and non-staff NPC streams (from
      // `stream_party_npc_response`). Pre-fix this hardcoded `'user'`
      // for the non-Tavian branch, which suppressed the typewriter for
      // roster NPC streams. Route via `event.isNpc` + staff-id check:
      // staff → matching type, non-staff NPC → `'npc'` (typewriters).
      // Falls back to `'user'` only when the wire flag explicitly says
      // PC (current callers never do this).
      const msgType: ChatMessageType = event.isNpc
        ? (staffMessageType(event.characterId) ?? 'npc')
        : 'user';

      console.log('[party-stream-start]', {
        streamId: event.streamId,
        characterName: event.characterName,
        messageId,
        roomId,
      });

      // Typewriter-eligible streams (non-staff NPCs + Aldric/Kira/Grolt
      // staff) reveal at human cadence via `ProgressiveReveal`. Mirror
      // `lobby:npc-stream-start`'s setup in `npc-events.ts`: mark the
      // bubble `isQueuedReveal` + `isLiveStream`, and register it in
      // `revealingMessageIds[roomId]` so `useLobbyMessageQueue` wires
      // the `onRevealComplete` callback through. Tavian + non-NPC
      // streams keep the existing server-paced display.
      //
      // Room-not-yet-exists fallback: `state.addMessage` buffers into
      // `pendingPartyMessages` for `party-*` rooms during the
      // room-creation race. The explicit `set` block below requires the
      // room to exist already (it `map`s over `chatRooms` and would
      // silently drop the bubble otherwise), so route through
      // `state.addMessage` whenever the room isn't there — the
      // reveal-queue markers don't apply to a not-yet-mounted room
      // anyway, and the watchdog in `useLobbyMessageQueue` keeps things
      // unstuck if the stream-start races room creation.
      const HUMAN_TYPING_TYPES: ChatMessageType[] = ['npc', 'aldric', 'kira', 'grolt'];
      const isHumanTyping = HUMAN_TYPING_TYPES.includes(msgType);
      const roomExists = get().chatRooms.some((r) => r.id === roomId);

      if (!roomExists) {
        state.addMessage(roomId, {
          id: messageId,
          senderId: event.characterId,
          senderName: event.characterName,
          content: '',
          timestamp: new Date(event.sent_at),
          type: msgType,
          isStreaming: true,
          avatar: avatarUrl,
          avatarUrl,
          avatarEmoji: emoji,
        });
        set({
          activeStreams: {
            ...activeStreams,
            [event.streamId]: {
              streamId: event.streamId,
              roomId,
              messageId,
              chunks: [],
              characterId: event.characterId,
              characterName: event.characterName,
              avatarUrl,
              avatarEmoji: emoji,
              msgType,
            },
          },
        });
        return true;
      }

      set((s) => ({
        chatRooms: s.chatRooms.map((r) =>
          r.id === roomId
            ? {
                ...r,
                messages: [
                  ...r.messages,
                  {
                    id: messageId,
                    senderId: event.characterId,
                    senderName: event.characterName,
                    content: '',
                    timestamp: new Date(event.sent_at),
                    type: msgType,
                    isStreaming: true,
                    isQueuedReveal: isHumanTyping,
                    // Real WS stream — must NOT be killed by tab-switch
                    // / Escape / ArrowDown. Distinguishes this from
                    // canned reveal-queue messages.
                    isLiveStream: isHumanTyping,
                    avatar: avatarUrl,
                    avatarUrl,
                    avatarEmoji: emoji,
                  },
                ],
                unreadCount: r.unreadCount,
              }
            : r
        ),
        activeStreams: {
          ...s.activeStreams,
          [event.streamId]: {
            streamId: event.streamId,
            roomId,
            messageId,
            chunks: [],
            characterId: event.characterId,
            characterName: event.characterName,
            avatarUrl,
            avatarEmoji: emoji,
            msgType,
          },
        },
        // Without this, `ProgressiveReveal` mounts but has no
        // `onComplete` callback and the bubble never flips out of
        // `isQueuedReveal` mode after typing finishes.
        revealingMessageIds: isHumanTyping
          ? { ...s.revealingMessageIds, [roomId]: messageId }
          : s.revealingMessageIds,
      }));
      return true;
    }

    case 'lobby:party-stream-chunk': {
      const { completedStreamIds, activeStreams } = get();

      // Skip if stream already completed
      if (completedStreamIds[event.streamId]) {
        return true;
      }

      // Find this stream in activeStreams.
      // Note: appendToMessage only updates chatRooms, not pendingPartyMessages.
      // For buffered streams (room not yet created), chunks are lost — this is
      // intentional because stream-end carries the authoritative fullMessage.
      const stream = activeStreams[event.streamId];
      if (stream) {
        state.appendToMessage(stream.roomId, stream.messageId, event.chunk);
      }
      return true;
    }

    case 'lobby:party-stream-end': {
      // Skip if stream already completed (dedup)
      if (get().completedStreamIds[event.streamId]) {
        return true;
      }

      const stream = get().activeStreams[event.streamId];
      console.log('[party-stream-end]', {
        eventStreamId: event.streamId,
        fullMessageLen: event.fullMessage?.length,
        streamFound: !!stream,
        activeStreamCount: Object.keys(get().activeStreams).length,
      });

      if (stream) {
        const cleanedMessage = cleanNpcResponse(event.fullMessage);
        const isEmptyMessage = !cleanedMessage || cleanedMessage.trim().length === 0;

        // Check if room exists in chatRooms or if the message was buffered in pendingPartyMessages
        const roomExistsInChat = get().chatRooms.some((r) => r.id === stream.roomId);

        if (roomExistsInChat) {
          // Room exists in chatRooms — update message in place (atomic with stream tracking)
          const HUMAN_TYPING_TYPES: ChatMessageType[] = ['npc', 'aldric', 'kira', 'grolt'];
          const isHumanTyping =
            stream.msgType !== undefined && HUMAN_TYPING_TYPES.includes(stream.msgType);
          set((s) => {
            const { [event.streamId]: _rm, ...freshActiveStreams } = s.activeStreams;
            return {
              chatRooms: s.chatRooms.map((r) =>
                r.id === stream.roomId
                  ? {
                      ...r,
                      messages: isEmptyMessage
                        ? r.messages.filter((m) => m.id !== stream.messageId)
                        : r.messages.map((m) =>
                            m.id === stream.messageId
                              ? {
                                  ...m,
                                  content: cleanedMessage,
                                  isStreaming: false,
                                  // Clear `isLiveStream` so the user can
                                  // tab-switch / ArrowDown / Escape once
                                  // the WS wire is done — typewriter
                                  // catchup is just animation now.
                                  // Mirrors the npc-stream-end pattern.
                                  isLiveStream: false,
                                  rawContent: undefined,
                                }
                              : m
                          ),
                    }
                  : r
              ),
              activeStreams: freshActiveStreams,
              completedStreamIds: addCompletedStreamId(s.completedStreamIds, event.streamId),
              isWaitingForResponse: false,
              requestSentAt: null,
              // Empty-message + human-typing stream: bubble was filtered
              // out so ProgressiveReveal's `onComplete` never fires.
              // Clear the reveal-id pointer or `advanceRevealQueue`
              // stalls until the 5.5s watchdog force-finishes it.
              revealingMessageIds:
                isEmptyMessage && isHumanTyping
                  ? {
                      ...s.revealingMessageIds,
                      [stream.roomId]:
                        s.revealingMessageIds[stream.roomId] === stream.messageId
                          ? null
                          : s.revealingMessageIds[stream.roomId],
                    }
                  : s.revealingMessageIds,
            };
          });
        } else {
          // Room doesn't exist yet — message is buffered in pendingPartyMessages.
          // Update the placeholder in the buffer with final content (or remove if empty).
          set((s) => {
            const { [event.streamId]: _rm, ...freshActiveStreams } = s.activeStreams;
            const pending = s.pendingPartyMessages[stream.roomId];
            const basePatch = {
              activeStreams: freshActiveStreams,
              completedStreamIds: addCompletedStreamId(s.completedStreamIds, event.streamId),
              isWaitingForResponse: false,
              requestSentAt: null,
            };
            if (!pending) return basePatch;

            const updatedPending = isEmptyMessage
              ? pending.filter((m) => m.id !== stream.messageId)
              : pending.map((m) =>
                  m.id === stream.messageId
                    ? { ...m, content: cleanedMessage, isStreaming: false, rawContent: undefined }
                    : m
                );

            return {
              ...basePatch,
              pendingPartyMessages: {
                ...s.pendingPartyMessages,
                [stream.roomId]: updatedPending,
              },
            };
          });
        }
      } else {
        // Stream not found in activeStreams (e.g. stream-start was never received).
        // Nothing we can do without sender info — just update tracking state.
        set((s) => {
          const { [event.streamId]: _rm, ...freshActiveStreams } = s.activeStreams;
          return {
            activeStreams: freshActiveStreams,
            completedStreamIds: addCompletedStreamId(s.completedStreamIds, event.streamId),
            isWaitingForResponse: false,
            requestSentAt: null,
          };
        });
      }
      return true;
    }

    case 'lobby:party-tavian-response':
      if (event.streamId && state.completedStreamIds[event.streamId]) {
        return true;
      }
      addOrEnqueueMessage(state, `party-${event.partyId}`, {
        id: msgId('tavian'),
        senderId: 'tavian',
        senderName: 'Tavian',
        content: cleanNpcResponse(event.message),
        timestamp: new Date(event.sent_at),
        type: 'tavian',
        avatar: TAVIAN_AVATAR_URL,
        avatarUrl: TAVIAN_AVATAR_URL,
        avatarEmoji: getSpecialNpcEmoji('tavian') ?? '🧙‍♂️',
      });
      return true;

    // NPC chat message in party (from Rust backend)
    case 'lobby:party-chat-npc': {
      // Check if this is a special NPC (Tavian, Aldric, Kira) or a roster NPC
      const isSpecialNpc = Object.values(ActiveNPCEnum).includes(event.npcId as ActiveNPCEnum);
      const isTavian = event.npcId === ActiveNPCEnum.TAVIAN;

      // Get emoji - special NPCs have dedicated emojis, roster NPCs get default
      const npcEmoji = isSpecialNpc
        ? (getSpecialNpcEmoji(event.npcId as 'tavian' | 'aldric' | 'kira') ?? '🧙‍♂️')
        : '🧙‍♂️';

      // Look up avatar with fallback chain (matches lobby:party-chat)
      const party = state.parties.find((p) => p.id === event.partyId);
      const partyMember = party?.members?.find((m) => m.id === event.npcId);
      const npcChar = state.lobbyCharacters.find((c) => c.id === event.npcId);
      const storeChar = state.characters.find((c) => c.id === event.npcId);

      // Avatar resolution priority (matches mobile's LobbyPartyChatNpc
      // handler): Tavian's hardcoded URL → server-shipped `avatarUrl`
      // → server-shipped `portraitUrl` (PC portrait-only fallback) →
      // party-member roster → lobbyCharacters/character store. The two
      // server fields are new (see lobby/handlers/party/vote.rs
      // `send_npc_party_chat`) and let PCs whose `session.avatar_url`
      // isn't generated still surface their portrait instead of a
      // name initial.
      const npcAvatar = isTavian
        ? TAVIAN_AVATAR_URL
        : (event.avatarUrl ??
          event.portraitUrl ??
          partyMember?.avatarUrl ??
          resolveCharacterAvatarUrl(npcChar, storeChar));

      // Route by `event.isNpc` + staff-id — `narrate_char_preparation`
      // ships PC "✨ Prepared" narration through this variant via
      // `send_npc_party_chat`, so `npcId` can be a PC. Without checking
      // `isNpc` the PC bubble would surface as `'npc'` and typewriter.
      const msgType: ChatMessageType = event.isNpc
        ? (staffMessageType(event.npcId) ?? 'npc')
        : 'user';
      addOrEnqueueMessage(state, `party-${event.partyId}`, {
        id: msgId('npc'),
        senderId: event.npcId,
        senderName: event.npcName,
        content: cleanNpcResponse(event.message),
        timestamp: new Date(event.sent_at),
        type: msgType,
        avatar: npcAvatar,
        avatarUrl: npcAvatar,
        avatarEmoji: partyMember?.avatarEmoji || npcEmoji,
      });
      // Clear the "waiting for response" spinner — but ONLY if this party is
      // the user's active chat room. `isWaitingForResponse` is a single global
      // flag (lobbyStore.latency.ts), so clearing unconditionally would kill
      // the spinner for an unrelated in-flight request (Aldric, inner-voice,
      // etc.) whenever a background Tavian narration lands in ANY party.
      // The terminal-response case that motivated this clear (validation
      // rejection for /start-adventure, Tavian 1:1 prompts returning via party
      // chat) is always in the active party room — the user is watching.
      if (state.activeChatRoomId === `party-${event.partyId}`) {
        state.setWaitingForResponse(false);
      }
      return true;
    }

    case 'lobby:open-party-chat': {
      const openPartyRoomId = `party-${event.partyId}`;
      const party = state.parties.find((p) => p.id === event.partyId);
      const partyName = event.partyName || party?.name || 'Party';
      const openMemberCount = party?.members?.length ?? party?.memberCharacterIds?.length ?? 0;
      const existingRoom = state.chatRooms.find((r) => r.id === openPartyRoomId);
      if (!existingRoom) {
        state.addChatRoom({
          id: openPartyRoomId,
          name: partyName,
          participants: party?.memberCharacterIds || [],
          messages: [],
          unreadCount: 0,
          type: 'party',
          isTavianPresent: true,
          memberCount: openMemberCount,
        });
      } else {
        state.updateChatRoom(openPartyRoomId, { memberCount: openMemberCount });
      }
      state.setCurrentParty(event.partyId);
      // Joining a party consumes the pending invite (accept via banner button
      // or Tavian). Any other invites are moot once you're in a party, so
      // clear the banner wholesale.
      state.clearPendingInvites();
      // Only switch to party chat if user doesn't have a saved preference for a safe room
      const savedRoom = getPersistedActiveChatRoom();
      if (
        !savedRoom ||
        !isSafeToRestoreRoom(savedRoom) ||
        !state.chatRooms.some((r) => r.id === savedRoom)
      ) {
        state.switchChatRoom(openPartyRoomId);
      }
      return true;
    }

    case 'lobby:close-party-chat': {
      const closePartyRoomId = `party-${event.partyId}`;
      state.removeChatRoom(closePartyRoomId);
      if (state.currentPartyId === event.partyId) {
        set({ currentPartyId: null });
      }
      return true;
    }

    default:
      return false;
  }
}
